/**
 * Cálculos compartidos de una evaluación antropométrica.
 *
 * Se usan tanto en el diálogo manual (`NutritionPage`) como en la
 * importación desde Excel, para que ambos caminos produzcan exactamente
 * los mismos valores derivados.
 */

import {
  computeComposition,
  computeIndices,
  computeEnergy,
  computeMetabolicAge,
  computeVisceralFat,
  computeMuscleMass,
  ActivityLevel,
  CompFormula,
  DensityEquation,
  EnergyGoal,
  Sex,
} from './nutrition-formulas';

export interface ProgressCalcInput {
  sex: Sex | '';
  ageYears: number | null;
  weightKg: number | null;
  heightCm: number | null;
  waistCm: number | null;
  hipCm: number | null;
  formula: CompFormula;
  densityEquation: DensityEquation;
  skinfolds: Record<string, number | null>;
  manualBodyFatPct: number | null;
  activityLevel: ActivityLevel;
  goal: EnergyGoal;
  manualVisceralKg: number | null;
}

export interface ProgressTargets {
  target_calories: number | null;
  target_protein: number | null;
  target_carbs: number | null;
  target_fat: number | null;
}

export const computeProgressResults = (input: ProgressCalcInput) => {
  const sex = input.sex as Sex;
  const ageYears = input.ageYears || 0;
  const weightKg = input.weightKg;

  const composition = computeComposition({
    sex,
    ageYears,
    formula: input.formula,
    densityEquation: input.densityEquation,
    skinfolds: input.skinfolds,
    weightKg,
  });

  const bodyFat = composition.percentFat != null ? composition.percentFat : input.manualBodyFatPct;
  const fatMassKg =
    composition.fatMassKg != null
      ? composition.fatMassKg
      : weightKg && bodyFat != null
      ? Math.round(((weightKg * bodyFat) / 100) * 100) / 100
      : null;
  const leanMassKg =
    weightKg && fatMassKg != null ? Math.round((weightKg - fatMassKg) * 100) / 100 : null;

  const indices = computeIndices({
    weightKg,
    heightCm: input.heightCm,
    waistCm: input.waistCm,
    hipCm: input.hipCm,
  });

  const energy = computeEnergy({
    sex,
    ageYears,
    weightKg,
    heightCm: input.heightCm,
    leanMassKg,
    activityLevel: input.activityLevel,
    goal: input.goal,
  });

  const metabolicAge = computeMetabolicAge({ sex, ageYears, bmr: energy.bmr });

  const visceralFat = computeVisceralFat({
    sex,
    ageYears,
    fatMassKg,
    waistCm: input.waistCm,
    manualKg: input.manualVisceralKg,
  });

  const muscleMass = computeMuscleMass({ leanMassKg, weightKg });

  return { composition, bodyFat, fatMassKg, leanMassKg, indices, energy, metabolicAge, visceralFat, muscleMass };
};

export const targetsFromEnergy = (energy: ReturnType<typeof computeProgressResults>['energy']): ProgressTargets => ({
  target_calories: energy.targetCalories,
  target_protein: energy.proteinG,
  target_carbs: energy.carbsG,
  target_fat: energy.fatG,
});
